import { useState } from "react";
import api, { fmtMoney, toastErr } from "../api";
import { CheckCircle, XCircle, LockSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

export function ServiceQuoteView({ q, token, onChanged }) {
  const [option, setOption] = useState("");
  const [name, setName] = useState("");
  const [po, setPo] = useState("");
  const [agree, setAgree] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/public/quote/${token}/pdf`;
  const seasonal = q.quote_type !== "one_time";
  const actionable = ["sent", "viewed"].includes(q.status) && !q.expired;

  const decide = async (approve) => {
    if (approve) {
      if (!name.trim()) return toast.error("Type your full name to accept");
      if (seasonal && !option) return toast.error("Pick an option first");
      if (!agree) return toast.error("Please accept the quote and its terms first");
    }
    setBusy(true);
    try {
      const r = await api.post(`/public/quote/${token}/decide`, {
        approve, option_label: option || null, name, po_number: po, decline_reason: reason,
      });
      toast.success(approve ? "Quote accepted — the operator has been notified" : "Quote declined");
      if (approve && r.data.schedule_required) toast.success("The operator will contact you to schedule the work");
      onChanged?.();
    } catch (e) { toastErr(e); }
    setBusy(false);
  };

  return (
    <div className="card-flat space-y-4 p-6" data-testid="sq-page">
      <div>
        <p className="font-heading text-lg font-bold" data-testid="sq-title">{q.title}</p>
        <p className="text-xs text-muted-foreground" data-testid="sq-number">
          {q.quote_number} · issued {q.issue_date} · valid until {q.valid_until}
        </p>
        {(q.contact_name || q.service_address) && (
          <p className="mt-1 text-sm text-muted-foreground" data-testid="sq-for">
            For {q.contact_name}{q.company_name ? ` (${q.company_name})` : ""}{q.service_address ? ` · ${q.service_address}` : ""}
          </p>
        )}
      </div>

      {q.expired && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-center" data-testid="sq-expired">
          <LockSimple size={28} className="mx-auto text-amber-600" />
          <p className="mt-1 text-sm font-bold">This quote expired on {q.valid_until}.</p>
          <p className="text-xs text-muted-foreground">Contact {q.business_name} to renew it.</p>
        </div>
      )}
      {q.status === "superseded" && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-center" data-testid="sq-updated">
          <p className="text-sm font-bold">This quote has been updated.</p>
          {q.latest_url && (
            <a href={q.latest_url} data-testid="sq-latest-link"
              className="mt-2 inline-block rounded-md bg-primary px-4 py-2 text-sm font-bold text-primary-foreground">View the latest revision</a>
          )}
        </div>
      )}
      {q.status === "withdrawn" && (
        <div className="rounded-md border border-border p-4 text-center" data-testid="sq-withdrawn">
          <p className="text-sm font-bold">This quote has been withdrawn.</p>
          <p className="text-xs text-muted-foreground">Contact {q.business_name} with any questions.</p>
        </div>
      )}
      {q.status === "accepted" && (
        <div className="space-y-1 text-center" data-testid="sq-accepted">
          <CheckCircle size={40} weight="fill" className="mx-auto text-primary" />
          <p className="font-heading text-lg font-bold">
            Accepted{q.accepted_name ? ` by ${q.accepted_name}` : ""}{q.accepted_option ? ` — Option ${q.accepted_option}` : ""}
          </p>
          <p className="text-sm text-muted-foreground">
            {seasonal ? "The operator will confirm next steps." : "The operator will contact you to schedule the work."}
          </p>
        </div>
      )}
      {q.status === "declined" && (
        <div className="space-y-1 text-center" data-testid="sq-declined">
          <XCircle size={40} className="mx-auto text-muted-foreground" />
          <p className="font-heading text-lg font-bold">Declined</p>
        </div>
      )}

      {q.scope_intro && <p className="text-sm" data-testid="sq-scope">{q.scope_intro}</p>}
      {q.visit_inclusions?.length > 0 && (
        <ul className="list-disc space-y-1 pl-5 text-sm" data-testid="sq-bullets">
          {q.visit_inclusions.map((b, i) => <li key={i}>{b}</li>)}
        </ul>
      )}

      {seasonal && q.options?.length > 0 && (
        <div className="space-y-2" data-testid="sq-options">
          {q.options.map((o) => (
            <label key={o.label} data-testid={`sq-option-${o.label}`}
              className={`block rounded-md border-2 p-3 ${actionable ? "cursor-pointer" : ""} ${option === o.label ? "border-primary bg-primary/5" : "border-border"}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold">
                  {actionable && <input type="radio" className="mr-2" checked={option === o.label} onChange={() => setOption(o.label)} />}
                  Option {o.label}
                </span>
                <span className="font-heading text-lg font-bold">{fmtMoney(o.season_total)}</span>
              </div>
              <p className="mt-0.5 text-xs text-muted-foreground">{o.frequency}{o.visits ? ` · ${o.visits} visits` : ""} · {o.per_visit_text != null ? `$${Number(o.per_visit_text).toFixed(2)}/visit` : "—"}</p>
              {o.clauses?.map((c, i) => <p key={i} className="mt-0.5 text-xs text-muted-foreground">· {c}</p>)}
            </label>
          ))}
        </div>
      )}

      {!seasonal && (
        <div data-testid="sq-price-table">
          <div className="space-y-1">
            {(q.price_lines || []).map((l, i) => (
              <div key={i} className="flex justify-between border-b border-border pb-1 text-sm">
                <span>{l.description}{l.qty ? ` × ${l.qty}` : ""}</span>
                <span className="font-semibold">{fmtMoney((l.amount || 0) * (l.qty || 1))}</span>
              </div>
            ))}
          </div>
          {q.price_totals && (
            <div className="mt-2 space-y-1 text-sm">
              <div className="flex justify-between"><span>Subtotal</span><span data-testid="sq-subtotal">{fmtMoney(q.price_totals.subtotal)}</span></div>
              {q.show_tax && q.price_totals.tax > 0 && (
                <div className="flex justify-between"><span>{q.price_totals.tax_label} ({q.price_totals.tax_rate}%)</span>
                  <span data-testid="sq-tax">{fmtMoney(q.price_totals.tax)}</span></div>
              )}
              <div className="flex justify-between font-heading text-lg font-bold"><span>Total</span>
                <span data-testid="sq-total">{fmtMoney(q.price_totals.total)}</span></div>
            </div>
          )}
          {q.target_timing && <p className="mt-2 text-xs text-muted-foreground" data-testid="sq-timing">Target timing: {q.target_timing}</p>}
        </div>
      )}

      {q.comparison_note && <p className="rounded-md bg-muted p-2 text-xs" data-testid="sq-comparison">{q.comparison_note}</p>}
      {q.season_text && <p className="text-sm" data-testid="sq-season">{q.season_text}</p>}
      {q.billing_text && <p className="text-sm" data-testid="sq-billing">{q.billing_text}</p>}
      {q.exclusions_text && <p className="text-xs text-muted-foreground" data-testid="sq-exclusions">Not included: {q.exclusions_text}</p>}
      {q.terms?.length > 0 && (
        <ul className="list-disc space-y-1 pl-5 text-xs text-muted-foreground" data-testid="sq-terms">
          {q.terms.map((t, i) => <li key={i}>{t}</li>)}
        </ul>
      )}
      {q.closing_text && <p className="text-sm" data-testid="sq-closing">{q.closing_text}</p>}

      <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="sq-download-pdf"
        className="block rounded-md border-2 border-primary py-2.5 text-center text-sm font-bold text-primary">Download PDF</a>

      {actionable && (
        <div className="space-y-2 border-t border-border pt-3" data-testid="sq-accept-form">
          <input data-testid="sq-accept-name" className="field-input w-full" placeholder="Type your full name (this is your signature)"
            value={name} onChange={(e) => setName(e.target.value)} />
          <input data-testid="sq-po" className="field-input w-full" placeholder="PO number (optional)"
            value={po} onChange={(e) => setPo(e.target.value)} />
          <label className="flex items-start gap-2 text-xs">
            <input type="checkbox" data-testid="sq-agree" className="mt-0.5" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
            I accept this quote and its terms
          </label>
          {!declining ? (
            <div className="grid grid-cols-2 gap-2">
              <button data-testid="sq-accept-btn" onClick={() => decide(true)} disabled={busy}
                className="h-14 rounded-md bg-primary font-bold text-primary-foreground disabled:opacity-40">Accept</button>
              <button data-testid="sq-decline-btn" onClick={() => setDeclining(true)} disabled={busy}
                className="h-14 rounded-md border border-destructive font-bold text-destructive disabled:opacity-40">Decline</button>
            </div>
          ) : (
            <div className="space-y-2">
              <input data-testid="sq-decline-reason" className="field-input w-full" placeholder="Reason (optional)"
                value={reason} onChange={(e) => setReason(e.target.value)} />
              <button data-testid="sq-decline-confirm" onClick={() => decide(false)} disabled={busy}
                className="h-12 w-full rounded-md border border-destructive font-bold text-destructive disabled:opacity-40">Confirm decline</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
